import React, { useMemo, useState } from "react";
import { Modal } from "@/components/Modal";
import { Search, Heart, Trophy } from "lucide-react";

const CATS = [
  { id: "stationery", label: "Stationery" },
  { id: "object", label: "School Objects" },
  { id: "game", label: "Classroom Games" },
];

const Bar = ({ label, have, total }) => (
  <div>
    <div className="mb-1 flex justify-between text-xs font-bold text-kirana-brown">
      <span>{label}</span><span>{have} / {total}</span>
    </div>
    <div className="h-2.5 overflow-hidden rounded-full border-2 border-kirana-brown bg-kirana-cream">
      <div className="h-full bg-kirana-red transition-all" style={{ width: `${total ? (have / total) * 100 : 0}%` }} />
    </div>
  </div>
);

export const SchoolCollectionPanel = ({ open, onClose, items, memories, onSelect, title = "My School Memories", simple = false }) => {
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");

  const counts = useMemo(() => {
    const c = {};
    CATS.forEach((k) => {
      const list = items.filter((i) => i.category === k.id);
      c[k.id] = { total: list.length, have: list.filter((i) => memories.includes(i.id)).length };
    });
    return c;
  }, [items, memories]);

  const total = items.length;
  const have = memories.length;
  const complete = total > 0 && have >= total;

  const filtered = items.filter(
    (i) => (!cat || i.category === cat) && (!q.trim() || i.name.toLowerCase().includes(q.toLowerCase()))
  );

  return (
    <Modal open={open} onClose={onClose} testId="school-collection-panel" maxWidth="max-w-4xl">
      <div className="p-5 sm:p-7">
        <h2 className="font-signboard flex items-center gap-2 text-3xl font-bold text-kirana-brown">
          <Heart className="h-6 w-6 fill-kirana-red text-kirana-red" /> {title}
        </h2>
        {!simple && (
          <>
            <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
              {CATS.map((k) => <Bar key={k.id} label={k.label} have={counts[k.id].have} total={counts[k.id].total} />)}
            </div>
            <div className="mt-3" data-testid="school-progress">
              <Bar label="Total" have={have} total={total} />
            </div>
            {complete && (
              <div className="mt-3 flex items-center gap-2 rounded-md border-2 border-kirana-green bg-kirana-green/10 px-3 py-2 font-bold text-kirana-green">
                <Trophy className="h-5 w-5" /> Full marks! You remembered your whole school bag. ❤️
              </div>
            )}
          </>
        )}

        <div className="mt-4 flex items-center gap-2 rounded-md border-2 border-kirana-brown bg-kirana-cream px-3 py-1.5">
          <Search className="h-4 w-4 text-kirana-brown/60" />
          <input data-testid="school-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…"
            className="w-full bg-transparent text-sm text-kirana-brown outline-none placeholder:text-kirana-brown/40" />
        </div>
        {!simple && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {CATS.map((k) => (
              <button key={k.id} data-testid={`school-filter-${k.id}`} onClick={() => setCat(cat === k.id ? "" : k.id)}
                className={`rounded-full border-2 border-kirana-brown px-3 py-1 text-xs font-bold ${cat === k.id ? "bg-kirana-red text-white" : "bg-kirana-cream text-kirana-brown hover:bg-kirana-gold"}`}>
                {k.label}
              </button>
            ))}
          </div>
        )}

        <div className="mt-4 grid max-h-[42vh] grid-cols-2 gap-3 overflow-y-auto scrollbar-thin pr-1 sm:grid-cols-3 lg:grid-cols-4">
          {filtered.map((i) => {
            const u = memories.includes(i.id);
            const img = i.image_url || (i.images && i.images[0]);
            return (
              <button key={i.id} data-testid={`school-item-${i.id}`} onClick={() => onSelect(i)}
                className="group relative flex flex-col overflow-hidden rounded-md border-[3px] border-kirana-brown text-left transition-transform hover:scale-[1.04] hard-shadow-sm"
                style={{ background: i.placeholder_color }}>
                <div className="flex h-16 items-center justify-center p-1">
                  {img ? <img src={img} alt={i.name} className="h-full w-full object-contain" /> :
                    <span className="font-signboard text-center text-[11px] font-bold text-white drop-shadow">{i.name}</span>}
                </div>
                <div className="bg-card px-1.5 py-1">
                  <span className="line-clamp-1 text-[11px] font-bold text-kirana-brown">{i.name}</span>
                </div>
                {u && <span className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full border-2 border-kirana-brown bg-kirana-cream"><Heart className="h-3 w-3 fill-kirana-red text-kirana-red" /></span>}
              </button>
            );
          })}
        </div>
      </div>
    </Modal>
  );
};
